import { Decimal } from "../decimal"
import { Money } from "./money"

// allocateByRoundingEach rounds each share independently - not guaranteed to sum back to amount.
export function allocateByRoundingEach(
  amount: Money,
  weights: Decimal[]
): Money[] {
  const totalWeight = weights.reduce((sum, w) => sum.add(w), Decimal.zero())
  if (totalWeight.isZero()) {
    return weights.map(() => Money.zero())
  }

  return weights.map((w) => amount.mult(w.div(totalWeight)))
}

// allocateByLargestRemainder floors every share's ideal cents,
// then hands the leftover cents to whichever shares had the largest remainder.
export function allocateByLargestRemainder(
  amount: Money,
  weights: Decimal[]
): Money[] {
  const totalWeight = weights.reduce((sum, w) => sum.add(w), Decimal.zero())
  if (totalWeight.isZero()) {
    return weights.map(() => Money.zero())
  }

  // Decimal.int() truncates toward zero, so allocate the absolute value and restore the sign at the end.
  const isNegative = amount.isNegative()
  const totalCents = amount.absolute().cents
  const totalCentsDecimal = Decimal.fromBigSource(totalCents.toString())
  const idealCents = weights.map((w) =>
    totalCentsDecimal.mult(w).div(totalWeight)
  )
  const flooredCents = idealCents.map((c) => c.int())
  const allocatedCents = flooredCents.reduce((sum, c) => sum + c, 0n)
  const leftoverCents = Number(totalCents - allocatedCents)

  const byLargestRemainder = idealCents
    .map((c, i) => ({
      index: i,
      remainder: c.sub(Decimal.fromBigSource(flooredCents[i].toString())),
    }))
    .sort((a, b) => b.remainder.cmp(a.remainder))

  const cents = [...flooredCents]
  for (let i = 0; i < leftoverCents; i++) {
    cents[byLargestRemainder[i].index] += 1n
  }

  return cents.map((c) => Money.fromCents(isNegative ? -c : c))
}
